import { useEffect, useRef, useState } from "react";
import { RPCCloseCodes } from "@discord/embedded-app-sdk";
import { getSDK } from "../discord/activity.js";

interface OpenInBrowserButtonProps {
  /** App slug, namespaces the saved preference, e.g. "deltatape". */
  app: string;
  /** The app's browser-mode URL (VITE_BROWSER_URL). Empty ⇒ renders nothing. */
  browserUrl: string;
}

const prefKey = (app: string): string => `2skies.${app}.autoCloseOnPopout`;
function getAutoClose(app: string): boolean {
  try {
    return localStorage.getItem(prefKey(app)) === "1";
  } catch {
    return false;
  }
}
function setAutoClose(app: string, on: boolean): void {
  try {
    if (on) localStorage.setItem(prefKey(app), "1");
    else localStorage.removeItem(prefKey(app));
  } catch {
    /* storage refused — they will just see the notice again */
  }
}

/**
 * Inside the Discord Activity: open this app in the member's normal browser so
 * Discord is free for other Activities (Discord allows one at a time). After
 * opening, explain and offer to close the Discord copy; a tick-box makes that
 * automatic next time. Render only in embedded mode (the caller decides).
 */
export function OpenInBrowserButton({ app, browserUrl }: OpenInBrowserButtonProps): JSX.Element | null {
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [remember, setRemember] = useState(false);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (noticeOpen) closeBtnRef.current?.focus();
  }, [noticeOpen]);
  if (!browserUrl) return null;

  const closeActivity = (): void => {
    getSDK()?.close(RPCCloseCodes.CLOSE_NORMAL, "Opened in browser");
  };

  const keepOpen = (): void => {
    setAutoClose(app, remember);
    setNoticeOpen(false);
  };

  async function handleClick(): Promise<void> {
    const sdk = getSDK();
    if (!sdk) return;
    let res: { opened: boolean | null };
    try {
      res = await sdk.commands.openExternalLink({ url: browserUrl });
    } catch (err) {
      console.warn("[popout] openExternalLink failed", err);
      return;
    }
    if (res.opened === false) return; // member cancelled Discord's prompt
    if (getAutoClose(app)) {
      closeActivity();
      return;
    }
    setNoticeOpen(true);
  }

  return (
    <>
      <button
        type="button"
        className="popout-btn"
        title="Open in your web browser so Discord stays free for other apps"
        onClick={() => void handleClick()}
      >
        Open in browser
      </button>
      {noticeOpen ? (
        <div className="popout-notice__backdrop">
          <div className="popout-notice" role="dialog" aria-modal="true" aria-labelledby="popout-notice-title"
            onKeyDown={(e) => { if (e.key === "Escape") keepOpen(); }}
          >
            <p id="popout-notice-title" className="popout-notice__title">This app is now open in your browser.</p>
            <p className="popout-notice__text">You can close this Discord window to use the other apps.</p>
            <label className="popout-notice__check">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
              Don&apos;t show this again (close automatically next time)
            </label>
            <div className="popout-notice__actions">
              <button
                type="button"
                ref={closeBtnRef}
                className="popout-notice__primary"
                onClick={() => {
                  setAutoClose(app, remember);
                  closeActivity();
                }}
              >
                Close this window
              </button>
              <button
                type="button"
                className="popout-notice__secondary"
                onClick={keepOpen}
              >
                Keep it open
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
